import { apiFetch } from '@/api/client'
import type { TradeFairState } from '@/api/tradeFairs'

/** The fair the overview is about: the one running today, or the next one to come. */
export type OverviewTradeFair = {
  id: string
  name: string
  city: string
  startDate: string
  endDate: string
  hall: string | null
  booth: string | null
  state: TradeFairState
}

/**
 * One request for the whole first screen.
 *
 * Every count is the backend's answer for **this** caller: a salesperson's totals are their own
 * leads, an administrator's are everybody's. Counted in the query, like the list, so the number
 * here and the rows there cannot disagree.
 */
export type Overview = {
  /** Null only when no fair is running and none is upcoming. */
  tradeFair: OverviewTradeFair | null
  /** The business date the figures were taken on. */
  today: string
  leadsTotal: number
  leadsAtTradeFair: number
  notLinkedToCustomer: number
}

export function loadOverview(signal?: AbortSignal): Promise<Overview> {
  return apiFetch<Overview>('/api/overview', { signal })
}

/** One day of the chart: an ISO date and how many leads were captured on it. */
export type LeadsOnDay = {
  date: string
  leads: number
}

/**
 * A trade fair's days and how many of its leads fall on each, oldest first.
 *
 * The window is the fair's own period: a chart that ran to today would open with empty columns for
 * the days before the doors did. Every day in between is present, the empty ones included — the
 * backend fills the gaps so a quiet Tuesday is drawn as a quiet Tuesday and not as a straight line
 * between Monday and Wednesday.
 *
 * Counted by the day of the conversation, not by when the row was written: a lead typed up on the
 * train home belongs to the day at the stand.
 *
 * Its own request rather than part of `Overview`, because it is a series and not a figure.
 */
export type LeadsPerDay = {
  from: string
  to: string
  /** What `days` adds up to: the fair's leads that fall on its own days. */
  total: number
  days: LeadsOnDay[]
}

export function loadLeadsPerDay(tradeFairId: string, signal?: AbortSignal): Promise<LeadsPerDay> {
  const parameters = new URLSearchParams({ tradeFairId })
  return apiFetch<LeadsPerDay>(`/api/overview/leads-per-day?${parameters}`, { signal })
}
